import { useContext, useEffect, useState } from "react";
import { AuthContext } from "../context/AuthContext";
import { db, auth } from "../firebase";
import { Search } from "lucide-react";
import {
  collection,
  getDocs,
  deleteDoc,
  doc,
  query,
  where,
  getDoc,
} from "firebase/firestore";
import { signOut } from "firebase/auth";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import ConfirmModal from "../components/ConfirmModal";
import { LogOut, Droplet, Briefcase, Car, User, ChevronRight, Trash2, Edit3, Settings, ShieldCheck } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { Helmet } from 'react-helmet-async';

export default function Profile() {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();

  const [donorData, setDonorData] = useState(null);
  const [labourData, setLabourData] = useState(null);
  const [driverData, setDriverData] = useState(null);
  const [loading, setLoading] = useState(true);

  // Modal States
  const [modalOpen, setModalOpen] = useState(false);
  const [deleteConfig, setDeleteConfig] = useState({ id: null, collection: "" });

  const handleLogout = async () => {
    await signOut(auth);
    toast.success("Logged out 👋");
    navigate("/");
  };

  const fetchUserActivities = async () => {
    if (!user) return;
    setLoading(true);
    try {
      // 1. Fetch Blood Donor Data
      const donorQ = query(collection(db, "donors"), where("userId", "==", user.uid));
      const donorRes = await getDocs(donorQ);
      if (!donorRes.empty) setDonorData({ id: donorRes.docs[0].id, ...donorRes.docs[0].data() });
      else setDonorData(null);

      // 2. Fetch Labour Data
      const labourRef = doc(db, "labours", user.uid);
      const labourSnap = await getDoc(labourRef);
      if (labourSnap.exists()) setLabourData({ id: labourSnap.id, ...labourSnap.data() });
      else setLabourData(null);

      // 3. Fetch Driver Data
      const driverRef = doc(db, "drivers", user.uid);
      const driverSnap = await getDoc(driverRef);
      if (driverSnap.exists()) setDriverData({ id: driverSnap.id, ...driverSnap.data() });
      else setDriverData(null);

    } catch (error) {
      console.error("Error fetching profile activities:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUserActivities();
  }, [user]);

  const confirmDelete = (id, collectionName) => {
    setDeleteConfig({ id, collection: collectionName });
    setModalOpen(true);
  };

  const handleDelete = async () => {
    setModalOpen(false);
    try {
      await deleteDoc(doc(db, deleteConfig.collection, deleteConfig.id));
      toast.success("നീക്കം ചെയ്തു 🗑️");
      fetchUserActivities(); // Refresh
    } catch (err) {
      toast.error("Error deleting item");
    }
  };

  if (!user) return null;

  return (
    <>
      <Helmet>
        <title>My Profile | Info Payangadi</title>
        <meta name="description" content="Manage your registrations and profile on Info Payangadi." />
      </Helmet>

      <div className="min-h-screen bg-[#F8FAFC] pb-32">
        
        {/* 🌟 1. PREMIUM USER HEADER 🌟 */}
        <div className="bg-[linear-gradient(110deg,#1E3A8A_0%,#2563EB_50%,#3B82F6_100%)] rounded-b-[3rem] px-6 pt-10 pb-12 shadow-[0_20px_40px_-15px_rgba(37,99,235,0.4)] relative overflow-hidden">
          
          <div className="absolute top-[-20%] right-[-10%] w-[300px] h-[300px] bg-white/10 rounded-full blur-3xl pointer-events-none"></div>
          <div className="absolute bottom-[-10%] left-[-10%] w-[250px] h-[250px] bg-white/5 rounded-full blur-3xl pointer-events-none"></div>

          <motion.div 
            initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}
            className="relative z-10 max-w-md mx-auto flex flex-col items-center text-center"
          >
            <div className="w-24 h-24 bg-white/10 backdrop-blur-md rounded-full flex items-center justify-center mb-4 border border-white/20 shadow-inner relative">
              <User size={44} className="text-white" strokeWidth={1.5} />
              {/* Green active dot */}
              <div className="absolute bottom-1 right-1 w-5 h-5 bg-emerald-400 border-4 border-[#2563EB] rounded-full"></div>
            </div>
            
            <h2 className="text-xl sm:text-2xl font-black text-white break-all max-w-[90%] tracking-tight">
              {user.email}
            </h2>
            
            <div className="flex items-center gap-1.5 mt-2 bg-white/10 backdrop-blur-sm px-3 py-1 rounded-full border border-white/10">
              <ShieldCheck size={14} className="text-cyan-300" />
              <p className="text-cyan-50 text-[10px] sm:text-xs font-bold uppercase tracking-widest">Verified Account</p>
            </div>

            <button
              onClick={handleLogout}
              className="mt-6 flex items-center justify-center gap-2 bg-white/10 hover:bg-red-500 hover:text-white hover:border-red-500 text-white/90 px-6 py-2.5 rounded-2xl text-sm font-bold transition-all duration-300 border border-white/20"
            >
              <LogOut size={16} strokeWidth={2.5} /> Logout
            </button>
          </motion.div>
        </div>

        {/* 🌟 2. QUICK ACCESS LINKS 🌟 */}
        <div className="max-w-md mx-auto grid grid-cols-3 gap-3 px-4 -mt-6 relative z-20">
          {[
            { icon: Droplet, color: "text-red-500", bg: "bg-white", border: "border-red-100", path: "/donors", name: "Donors" },
            { icon: Briefcase, color: "text-indigo-500", bg: "bg-white", border: "border-indigo-100", path: "/labours", name: "Labours" },
            { icon: Car, color: "text-amber-500", bg: "bg-white", border: "border-amber-100", path: "/drivers", name: "Drivers" }
          ].map((item, i) => (
            <motion.button 
              key={i} 
              initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 + (i * 0.05) }}
              whileHover={{ y: -2, scale: 1.02 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => navigate(item.path)}
              className={`${item.bg} p-4 rounded-[1.5rem] flex flex-col items-center justify-center gap-2 shadow-[0_8px_30px_rgb(0,0,0,0.06)] border ${item.border} active:scale-95 transition-all`}
            >
              <div className={`${item.color} bg-slate-50 p-2.5 rounded-xl`}>
                <item.icon size={22} strokeWidth={2.5} />
              </div>
              <span className="text-[10px] font-black text-slate-600 tracking-wider uppercase">{item.name}</span>
            </motion.button>
          ))}
        </div>

        {/* 🌟 3. MY REGISTRATIONS SECTION 🌟 */}
        <div className="max-w-md mx-auto mt-10 px-4">
          <div className="flex items-center justify-between mb-5 px-1">
            <h3 className="text-lg font-black text-slate-800 flex items-center gap-2 tracking-tight">
              <Settings size={20} className="text-blue-500" strokeWidth={2.5} /> My Registrations
            </h3>
          </div>

          {loading ? (
            <div className="text-center py-12 flex flex-col items-center">
              <div className="w-10 h-10 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin mb-3"></div>
              <span className="text-slate-400 font-bold text-sm">Fetching your data...</span>
            </div>
          ) : (
            <div className="space-y-4">
              
              <AnimatePresence>
                {/* 🩸 BLOOD DONOR CARD */}
                {donorData && (
                  <ActivityCard 
                    key="donor"
                    title="Blood Donor Profile"
                    subtitle={`Blood Group: ${donorData.blood}`}
                    icon={Droplet}
                    color="text-red-500"
                    bg="bg-red-50"
                    onEdit={() => navigate("/donors")}
                    onDelete={() => confirmDelete(donorData.id, "donors")}
                  />
                )}

                {/* 🛠️ LABOUR CARD */}
                {labourData && (
                  <ActivityCard 
                    key="labour"
                    title="Labour Profile"
                    subtitle={`Category: ${labourData.category}`}
                    icon={Briefcase}
                    color="text-indigo-500"
                    bg="bg-indigo-50"
                    onEdit={() => navigate("/labours")}
                    onDelete={() => confirmDelete(labourData.id, "labours")}
                  />
                )}

                {/* 🚖 DRIVER CARD */}
                {driverData && (
                  <ActivityCard 
                    key="driver"
                    title="Driver Profile"
                    subtitle={`Vehicle: ${driverData.vehicleType}`}
                    icon={Car}
                    color="text-amber-500"
                    bg="bg-amber-50"
                    onEdit={() => navigate("/drivers")}
                    onDelete={() => confirmDelete(driverData.id, "drivers")}
                  />
                )}

                {/* NO DATA STATE */}
                {!donorData && !labourData && !driverData && (
                  <motion.div 
                    initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
                    className="bg-white p-10 rounded-[2.5rem] border border-dashed border-slate-300 text-center shadow-sm"
                  >
                    <div className="bg-slate-50 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Search size={28} className="text-slate-300" />
                    </div>
                    <h4 className="text-slate-700 font-black text-lg">No Registrations Yet</h4>
                    <p className="text-slate-400 font-medium text-sm mt-1 mb-5 leading-relaxed">
                      You haven't registered as a donor, worker, or driver.
                    </p>
                    <button 
                      onClick={() => navigate("/")}
                      className="bg-blue-50 hover:bg-blue-600 text-blue-600 hover:text-white px-5 py-2.5 rounded-full font-black text-sm flex items-center justify-center mx-auto gap-1.5 transition-colors"
                    >
                      Browse Services <ChevronRight size={16} strokeWidth={3} />
                    </button>
                  </motion.div>
                )}
              </AnimatePresence>

            </div>
          )}
        </div>

        {/* 🌟 DELETE MODAL 🌟 */}
        <ConfirmModal
          isOpen={modalOpen}
          message="Are you sure you want to delete this registration? This action cannot be undone."
          onClose={() => setModalOpen(false)}
          onConfirm={handleDelete}
        />
      </div>
    </>
  );
}

// 🌟 REUSABLE ACTIVITY CARD COMPONENT (Premium Redesign)
function ActivityCard({ title, subtitle, icon: Icon, color, bg, onEdit, onDelete }) {
  return (
    <motion.div 
      layout
      initial={{ opacity: 0, y: 15 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.9 }}
      whileHover={{ scale: 1.01 }}
      className="bg-white p-5 rounded-[2rem] shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-slate-100 flex items-center justify-between group transition-all"
    >
      <div className="flex items-center gap-4">
        <div className={`${bg} ${color} p-3.5 rounded-2xl shadow-sm border border-white/50 group-hover:scale-110 transition-transform duration-300`}>
          <Icon size={22} strokeWidth={2.5} />
        </div>
        <div>
          <h4 className="font-black text-slate-800 text-base tracking-tight">{title}</h4>
          <p className="text-slate-400 text-xs font-bold uppercase tracking-wider mt-0.5">{subtitle}</p>
        </div>
      </div>
      
      <div className="flex gap-1.5">
        <button 
          onClick={onEdit} 
          className="p-2.5 bg-slate-50 text-slate-400 hover:bg-blue-50 hover:text-blue-600 rounded-xl transition-all duration-200"
          aria-label="Edit"
        >
          <Edit3 size={18} strokeWidth={2.5} />
        </button>
        <button 
          onClick={onDelete} 
          className="p-2.5 bg-slate-50 text-slate-400 hover:bg-red-50 hover:text-red-600 rounded-xl transition-all duration-200"
          aria-label="Delete"
        >
          <Trash2 size={18} strokeWidth={2.5} />
        </button>
      </div>
    </motion.div>
  );
}